import { useEffect, useRef } from 'react';
// Deep import, not the `@/shared/ui` barrel: shared/ui components import the
// shared/lib barrel, so pulling the barrel here would make lib ↔ ui circular.
import { toastStore } from '@/shared/ui/toast/toast-store';

// App-wide toast helper for mutations. Toasts live at the ui/ call site,
// never inside the api/ mutation hooks. Two shapes:
//   • toastMutation(...)     — spread into `mutate(vars, ...)` (closes a
//     modal / navigates / optimistically unmounts on success).
//   • useMutationToasts(...) — for a component that stays mounted and
//     reads `mutation.data` / `mutation.error` (in-place field saves).

type MutationErrorLike = {
  message: string;
  // tRPC client errors carry the server's error code here. Present on real
  // `TRPCClientError`s, absent on hand-rolled test doubles — hence optional.
  data?: { code?: string } | null;
};

// Codes whose `message` the server authors FOR the user. Everything else
// (NOT_FOUND, UNAUTHORIZED, INTERNAL_SERVER_ERROR) carries a technical string
// that would be noise in a toast.
const USER_FACING_CODES = new Set(['BAD_REQUEST', 'CONFLICT']);

/**
 * Error resolver that shows the server's own explanation when there is one,
 * and the caller's generic line otherwise.
 *
 * Use it wherever a mutation can fail for a reason the user can actually fix —
 * a generic "Couldn't assign guide" tells them nothing about WHICH guide or
 * WHY, which is the whole difference between an actionable error and a dead
 * end. A zod failure also surfaces as BAD_REQUEST but serialises to a JSON
 * array, so a message that looks like a payload falls back too.
 */
export const backendMessageOr =
  (fallback: string) =>
  (error: MutationErrorLike): string => {
    if (!USER_FACING_CODES.has(error.data?.code ?? '')) return fallback;
    const message = error.message?.trim() ?? '';
    if (message.length === 0) return fallback;
    return /^[[{]/.test(message) ? fallback : message;
  };

// `undefined` → toast the raw backend message; a string → that fixed
// message; a resolver → derive it from the error; `null` → suppress the
// error toast entirely (e.g. a modal that already renders the error
// inline, so a toast would double-surface it).
type ErrorMessage =
  | string
  | ((error: MutationErrorLike) => string)
  | null
  | undefined;

type ToastMessages = {
  success: string;
  error?: ErrorMessage;
};

const resolveErrorMessage = (
  error: MutationErrorLike,
  spec: ErrorMessage
): string | null => {
  if (spec === null) return null;
  if (spec === undefined) return error.message;
  return typeof spec === 'function' ? spec(error) : spec;
};

export const toastMutation = (
  messages: ToastMessages,
  // Compose the caller's own success/error logic — closing a modal,
  // reverting optimistic state, etc.
  extra?: {
    onSuccess?: () => void;
    onError?: (error: MutationErrorLike) => void;
  }
) => ({
  onSuccess: () => {
    toastStore.getState().show({ message: messages.success });
    extra?.onSuccess?.();
  },
  onError: (error: MutationErrorLike) => {
    const message = resolveErrorMessage(error, messages.error);
    if (message !== null) toastStore.getState().show({ message });
    extra?.onError?.(error);
  },
});

export const useMutationToasts = (
  mutation: { data: unknown; error: MutationErrorLike | null },
  messages: ToastMessages
) => {
  // Keep the latest messages in a ref so callers can pass an inline object
  // literal without the effects re-firing on its identity change — they
  // fire only when the settled result (`data` / `error`) changes.
  const messagesRef = useRef(messages);
  messagesRef.current = messages;

  const { data, error } = mutation;

  useEffect(() => {
    if (!data) return;
    toastStore.getState().show({ message: messagesRef.current.success });
  }, [data]);

  useEffect(() => {
    if (!error) return;
    const message = resolveErrorMessage(error, messagesRef.current.error);
    if (message !== null) toastStore.getState().show({ message });
  }, [error]);
};
